import React, { useState } from 'react';
import './FoodSupplierDetails.css';
import {
  Button, TextField, FormControl, InputLabel, Select, MenuItem, Dialog,
  DialogActions, DialogContent, DialogTitle, Checkbox, ListItemText
} from '@mui/material';

const FoodSupplierDetails = ({ name, city, image }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    email: '',
    bookingDate: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [availableMenu, setAvailableMenu] = useState([]);

  // Weekly Menu Data
  const weeklyMenu = {
    '2024-10-01': {
      breakfast: { item: "Pancakes", price: "$5" },
      lunch: { item: "Grilled Chicken Salad", price: "$10" },
      dinner: { item: "Steak with Vegetables", price: "$15" },
    },
    '2024-10-02': {
      breakfast: { item: "Omelette", price: "$4" },
      lunch: { item: "Pasta with Pesto", price: "$9" },
      dinner: { item: "Baked Salmon", price: "$16" },
    },
    '2024-10-03': {
      breakfast: { item: "Fruit Salad and Yogurt", price: "$6" },
      lunch: { item: "Chicken Wrap", price: "$8" },
      dinner: { item: "Roast Beef with Mashed Potatoes", price: "$17" },
    },
    '2024-10-04': {
      breakfast: { item: "Avocado Toast", price: "$5" },
      lunch: { item: "Vegetable Stir-fry", price: "$9" },
      dinner: { item: "Grilled Shrimp Tacos", price: "$18" },
    },
    '2024-10-05': {
      breakfast: { item: "Smoothie Bowl", price: "$7" },
      lunch: { item: "Turkey Sandwich", price: "$10" },
      dinner: { item: "BBQ Ribs", price: "$20" },
    },
    '2024-10-06': {
      breakfast: { item: "Bagel with Cream Cheese", price: "$4" },
      lunch: { item: "Quinoa Salad", price: "$8" },
      dinner: { item: "Lasagna", price: "$15" },
    },
    '2024-10-07': {
      breakfast: { item: "French Toast", price: "$5" },
      lunch: { item: "Caesar Salad", price: "$9" },
      dinner: { item: "Pork Chops with Applesauce", price: "$19" },
    },
  };

  const handleBookSupplierClick = () => {
    setShowBookingForm(prevState => !prevState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));

    // Update available menu if the booking date changes
    if (name === 'bookingDate') {
      const selectedMenu = weeklyMenu[value];
      const menuItems = selectedMenu ? [
        selectedMenu.breakfast,
        selectedMenu.lunch,
        selectedMenu.dinner
      ] : [];
      setAvailableMenu(menuItems);
      setSelectedItems([]); // Reset selected items when date changes
    }
  };

  const handleItemChange = (event) => {
    const { value } = event.target;
    setSelectedItems(value);
  };

  const handleQuantityChange = (item, quantity) => {
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [item]: quantity
    }));
  };

  const renderWeeklyMenu = () => {
    return (
      <div>
        <h2>Weekly Menu</h2>
        {Object.keys(weeklyMenu).map((date) => (
          <div key={date} className="menu-day">
            <h3>{date}</h3>
            <p><strong>Breakfast:</strong> {weeklyMenu[date].breakfast.item} - {weeklyMenu[date].breakfast.price}</p>
            <p><strong>Lunch:</strong> {weeklyMenu[date].lunch.item} - {weeklyMenu[date].lunch.price}</p>
            <p><strong>Dinner:</strong> {weeklyMenu[date].dinner.item} - {weeklyMenu[date].dinner.price}</p>
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div>
            <h2>Overview</h2>
            <p>{name} offers a wide variety of meal options in {city}, catering to events and daily food delivery needs. Our weekly menu ensures fresh and healthy meals for all tastes.</p>
          </div>
        );
      case 'location':
        return (
          <div>
            <h2>Location</h2>
            <p>{name} is conveniently located in {city}, ensuring timely delivery across the city.</p>
          </div>
        );
      case 'menus':
        return renderWeeklyMenu(); // Display the weekly menu here
      case 'reviews':
        return (
          <div>
            <h2>Customer Reviews</h2>
            <p>"Delicious meals every day! Will order again." - Alice J.</p>
            <p>"Great quality and timely service." - Bob K.</p>
          </div>
        );
      default:
        return <div>Overview info goes here...</div>;
    }
  };

  return (
    <div className="food-supplier-details">
      <div className="food-supplier-details-container">
        <div className="food-supplier-image">
          <img src={image} alt={`${name}`} />
        </div>
        <Button
          onClick={handleBookSupplierClick}
          variant="outlined"
          color="primary"
          sx={{ fontSize: '12px', margin: '20px 0' }}
        >
          {showBookingForm ? 'Close Booking Form' : 'Book Food Supplier'}
        </Button>
        <Dialog
          open={showBookingForm}
          onClose={() => setShowBookingForm(false)}
          PaperProps={{ className: 'custom-dialog-paper' }}
        >
          <DialogTitle>Booking Form</DialogTitle>
          <DialogContent className="custom-dialog-content">
            <TextField
              label="Phone Number"
              variant="outlined"
              fullWidth
              margin="normal"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
            <TextField
              label="Email Address"
              type="email"
              variant="outlined"
              fullWidth
              margin="normal"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              label="Booking Date"
              type="date"
              variant="outlined"
              fullWidth
              margin="normal"
              name="bookingDate"
              value={formData.bookingDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Food Items</InputLabel>
              <Select
                multiple
                value={selectedItems}
                onChange={handleItemChange}
                renderValue={(selected) => selected.join(', ')}
              >
                {availableMenu.length > 0 ? (
                  availableMenu.map((menu, index) => (
                    <MenuItem key={index} value={menu.item}>
                      <Checkbox checked={selectedItems.indexOf(menu.item) > -1} />
                      <ListItemText primary={menu.item} secondary={menu.price} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No menu available for selected date</MenuItem>
                )}
              </Select>
            </FormControl>
            {selectedItems.map((item) => (
              <TextField
                key={item}
                label={`Quantity for ${item}`}
                type="number"
                variant="outlined"
                fullWidth
                margin="normal"
                value={quantities[item] || ''}
                onChange={(e) => handleQuantityChange(item, e.target.value)}
                InputProps={{ inputProps: { min: 1 } }} // Prevent 0 or negative values
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowBookingForm(false)} color="primary">Cancel</Button>
            <Button variant="contained" color="primary">Submit</Button>
          </DialogActions>
        </Dialog>
        <div className="tabs">
          <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>Overview</button>
          <button className={activeTab === 'location' ? 'active' : ''} onClick={() => setActiveTab('location')}>Location</button>
          <button className={activeTab === 'menus' ? 'active' : ''} onClick={() => setActiveTab('menus')}>Menus</button>
          <button className={activeTab === 'reviews' ? 'active' : ''} onClick={() => setActiveTab('reviews')}>Reviews</button>
        </div>
        <div className="tab-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default FoodSupplierDetails;
